import type { AxePurchaseState } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import { ACTIVE_STATES } from '~/common/constants';

export interface TeamsAccessSubscription {
  product_slug: string;
  purchase_state: string;
  instance_url?: string | null;
}

export interface TeamsAccessInput {
  isEnterpriseAdmin: boolean;
  subscriptions: readonly TeamsAccessSubscription[];
  /** The viewer's own Axe Monitor access, which the Monitor rule also requires. */
  monitorAccess: AxePurchaseState;
  flags: {
    teamsV1: boolean;
    teamsV2: boolean;
    reportsTeamsV1: boolean;
  };
}

const isActive = ({ purchase_state }: TeamsAccessSubscription): boolean =>
  ACTIVE_STATES.includes(purchase_state as AxePurchaseState);

const hasMonitorTeams = ({
  subscriptions,
  monitorAccess
}: TeamsAccessInput): boolean =>
  monitorAccess !== 'none' &&
  subscriptions.some(
    subscription =>
      subscription.product_slug === ProductSlugs.axeMonitor &&
      Boolean(subscription.instance_url) &&
      isActive(subscription)
  );

const hasReportsTeams = ({ subscriptions, flags }: TeamsAccessInput): boolean =>
  flags.reportsTeamsV1 &&
  subscriptions.some(
    subscription =>
      subscription.product_slug === ProductSlugs.axeReports &&
      isActive(subscription)
  );

/**
 * Whether the viewer may reach the Teams area. The nav entry and the Teams
 * routes must both decide through this, or one can show a link the other
 * redirects away from.
 *
 * With teams_v2 on, any enterprise admin qualifies whatever they subscribe
 * to: Teams only groups people, and each product still enforces its own
 * subscription. With it off, only paid or trialing Monitor and Reports
 * subscriptions qualify.
 */
export const canAccessTeams = (input: TeamsAccessInput): boolean => {
  if (!input.isEnterpriseAdmin || !input.flags.teamsV1) {
    return false;
  }

  return (
    input.flags.teamsV2 || hasMonitorTeams(input) || hasReportsTeams(input)
  );
};
